// AI formulas (owner decision 11/10): the AI packs are ACCESS TIERS.
//   base        the subscription alone — 10 Co-Gérant questions included per month (server:
//               AI_INCLUDED_QUESTIONS_PER_MONTH, sent as plan.included_questions.limit)
//   essentiel   « Essentiel »  — Co-Gérant questions + automatic night analyses
//   croissance  « Croissance » — + image / photo / voice features
//   pro         « Pro »        — + video, « analyse approfondie », everything
//
// The API refuses a feature outside the restaurant's formula with
//   403 { detail: { code: 'ai_tier_required', required_tier, feature, current_tier, … } }
// and the end of the included questions with
//   402 { detail: { code: 'included_questions_exhausted' | 'included_budget_reached', required_tier: 'essentiel' } }
// (backend app/services/ai_tiers.py). This module turns those answers into one event the
// app-wide modal (AiPlanGateModal) listens to — so every AI feature benefits without a line of its own.

export const TIERS = ['base', 'essentiel', 'croissance', 'pro'];
export const TIER_PACK = { essentiel: 'starter', croissance: 'growth', pro: 'pro' };
export const PACK_TIER = { starter: 'essentiel', growth: 'croissance', pro: 'pro' };
// i18n keys of the formula names (fr « Essentiel / Croissance / Pro », ar « الأساسية / النمو / برو »)
export const TIER_LABEL_KEYS = { base: 'Subscription', essentiel: 'Essential', croissance: 'Growth', pro: 'Pro' };
export const AI_PLANS_PAGE = '/admin/settings/ai-tokens';
export const AI_PLAN_EVENT = 'ods:ai-plan-required';
// What the server includes when it does not say (backend default AI_INCLUDED_QUESTIONS_PER_MONTH=10).
export const INCLUDED_QUESTIONS_DEFAULT = 10;

const QUESTION_CODES = new Set(['included_questions_exhausted', 'included_budget_reached']);

const detailOf = (err) => {
  const d = err?.response?.data?.detail;
  return d && typeof d === 'object' && !Array.isArray(d) ? d : null;
};

/**
 * The formula wall behind an API error, or null.
 * @returns {null | {kind: 'tier'|'questions'|'budget', requiredTier: string, feature: string|null, limit: number|null}}
 */
export function aiPlanWall(err) {
  const status = err?.response?.status;
  const d = detailOf(err);
  if (!d) return null;
  const code = d.code || d.error;
  if (status === 403 && code === 'ai_tier_required') {
    const requiredTier = TIERS.includes(d.required_tier) ? d.required_tier : 'croissance';
    return { kind: 'tier', requiredTier, feature: d.feature || null, limit: null };
  }
  if (status === 402 && QUESTION_CODES.has(code)) {
    return {
      kind: code === 'included_questions_exhausted' ? 'questions' : 'budget',
      requiredTier: TIERS.includes(d.required_tier) ? d.required_tier : 'essentiel',
      feature: null,
      limit: Number.isFinite(Number(d.limit)) && d.limit != null ? Number(d.limit) : null,
    };
  }
  return null;
}

export const isAiPlanWall = (err) => aiPlanWall(err) !== null;

/** The sentence shown for a wall, with `t` (i18next). */
export function aiPlanMessage(wall, t = (s, o) => (o ? s.replace(/\{\{(\w+)\}\}/g, (_, k) => o[k]) : s)) {
  if (!wall) return '';
  if (wall.kind === 'tier') {
    return t('This feature is included in the {{plan}} plan.', { plan: t(TIER_LABEL_KEYS[wall.requiredTier]) });
  }
  if (wall.kind === 'questions') {
    return t('The {{n}} questions included this month are used. Choose a plan to keep asking questions.', { n: wall.limit || INCLUDED_QUESTIONS_DEFAULT });
  }
  return t('The questions included in your subscription are used up for this month. Choose a plan to continue.');
}

// One modal per wall, even when a screen fires several AI calls at once.
let lastShown = 0;
const DEDUPE_MS = 1500;

/** Tell the app-wide modal (no-op outside a browser or for a request that explains it itself). */
export function announceAiPlanWall(err, now = Date.now()) {
  const wall = aiPlanWall(err);
  if (!wall) return null;
  // a screen that explains the formula itself (the Co-Gérant chat) asks for silence
  if (err?.config?.quietAiPlanModal || err?.config?.quietAiTokenToast) return wall;
  if (typeof window === 'undefined' || now - lastShown < DEDUPE_MS) return wall;
  lastShown = now;
  try {
    window.dispatchEvent(new CustomEvent(AI_PLAN_EVENT, { detail: wall }));
  } catch (_) { /* old browser — the caller still gets the rejection */ }
  return wall;
}

/**
 * Response interceptor: an AI action outside the restaurant's formula (403 ai_tier_required)
 * or past the included Co-Gérant questions (402) opens the app-wide « formule » modal — for
 * every AI feature, whichever axios instance made the call. The rejection is passed on untouched.
 * @param {import('axios').AxiosInstance} instance
 */
export const attachAiPlanGate = (instance) => {
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      try { announceAiPlanWall(error); } catch (_) { /* never mask the real error */ }
      return Promise.reject(error);
    },
  );
  return instance;
};

export const _resetAiPlanGateForTests = () => { lastShown = 0; };
